import React, { forwardRef, useState, useEffect } from 'react';
import { INPUT_TOKENS } from '../../../styles/UISystem';

/**
 * NameInput — large bold centered name input.
 * Used on detail pages and profile screen.
 *
 * @param {string} value - Current name value
 * @param {function} onChange - Change handler
 * @param {string} [placeholder='Add name'] - Placeholder text
 * @param {string} [focusStyle='always'] - 'always' (white bg + shadow) or 'dynamic' (transparent → white on focus)
 * @param {function} [onFocus] - Focus handler
 * @param {function} [onBlur] - Blur handler
 */

// Inject placeholder CSS once per page load
let styleInjected = false;
const injectPlaceholderStyle = () => {
  if (styleInjected) return;
  const style = document.createElement('style');
  style.textContent = `.ui-name-input::placeholder { color: #C7C7CC; font-weight: 400; }`;
  document.head.appendChild(style);
  styleInjected = true;
};

const NameInput = forwardRef(
  (
    { value, onChange, placeholder = 'Add name', focusStyle = 'always', onFocus, onBlur, ...rest },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = value && value.trim().length > 0;

    useEffect(() => {
      injectPlaceholderStyle();
    }, []);

    const isAlways = focusStyle === 'always';
    const showActive = isAlways || isFocused;

    const style = {
      width: '100%',
      maxWidth: '280px',
      fontSize: '24px',
      fontWeight: hasValue ? 700 : 400,
      color: '#000000',
      letterSpacing: '-0.3px',
      textAlign: 'center',
      border: 'none',
      outline: 'none',
      padding: showActive ? '8px 16px' : '8px 0',
      borderRadius: showActive ? '16px' : 0,
      backgroundColor: showActive ? '#FFFFFF' : 'transparent',
      boxShadow: showActive ? INPUT_TOKENS.PILL_SHADOW : 'none',
      boxSizing: 'border-box',
      ...(focusStyle === 'dynamic'
        ? {
            transition:
              'background 0.2s ease, box-shadow 0.2s ease, border-radius 0.2s ease, font-weight 0.1s ease',
          }
        : {}),
    };

    return (
      <input
        ref={ref}
        className="ui-name-input"
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoCapitalize="words"
        onFocus={(e) => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur?.(e);
        }}
        style={style}
        {...rest}
      />
    );
  }
);

NameInput.displayName = 'NameInput';

export { NameInput };
export default NameInput;
